import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import Logo from '../base/Logo';

const LANGS = [
  { code: 'az', label: 'AZ', full: 'Azərbaycanca' },
  { code: 'en', label: 'EN', full: 'English' },
  { code: 'ru', label: 'RU', full: 'Русский' },
];

const NAV_LINKS = [
  { key: 'nav.home', to: '/' },
  { key: 'nav.pricing', to: '/pricing' },
  { key: 'nav.support', to: '/support' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); setLangOpen(false); }, [location.pathname]);
  useEffect(() => {
    const close = () => setLangOpen(false);
    if (langOpen) window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [langOpen]);

  const currentLang = LANGS.find(l => l.code === i18n.language) ?? LANGS[0];

  return (
    <>
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-8">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-slate-500 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono">DC-1 Online</span>
            </span>
            <span className="hidden sm:flex items-center gap-1.5 text-slate-500 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono">99.9% Uptime</span>
            </span>
          </div>
          <span className="text-slate-600 text-xs hidden sm:block">support@emailaze.com</span>
        </div>
      </div>

      {/* Main navbar */}
      <header className="fixed top-8 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 mr-8">
              <Logo variant="light" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-0">
              {NAV_LINKS.map(({ key, to }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                      active ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t(key)}
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="flex-1" />

            {/* Right side */}
            <div className="hidden md:flex items-center gap-2">
              {/* Language dropdown */}
              <div className="relative" onClick={e => { e.stopPropagation(); setLangOpen(!langOpen); }}>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer">
                  <Globe size={14} />
                  <span className="font-medium">{currentLang.label}</span>
                  <ChevronDown size={12} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-50">
                    {LANGS.map(({ code, label, full }) => (
                      <button
                        key={code}
                        onClick={() => { i18n.changeLanguage(code); setLangOpen(false); }}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                          i18n.language === code
                            ? 'bg-primary-600 text-white'
                            : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                        }`}
                      >
                        <span>{full}</span>
                        <span className="font-mono text-xs opacity-60">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-slate-700 mx-1" />

              <Link
                to="/login"
                className="px-4 py-1.5 text-sm font-medium text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 rounded-lg transition-all"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors shadow-lg shadow-primary-900/30"
              >
                {t('nav.register')}
              </Link>
            </div>

            {/* Mobile button */}
            <button
              className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-800 bg-slate-900">
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map(({ key, to }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === to
                      ? 'bg-primary-600/20 text-primary-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {t(key)}
                </Link>
              ))}
              <div className="pt-3 border-t border-slate-800 space-y-2">
                <Link to="/login" className="flex">
                  <button className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-slate-300 border border-slate-700 hover:border-slate-500 hover:text-white transition-colors">
                    {t('nav.login')}
                  </button>
                </Link>
                <Link to="/register" className="flex">
                  <button className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-500 transition-colors">
                    {t('nav.register')}
                  </button>
                </Link>
              </div>
              {/* Mobile lang */}
              <div className="flex gap-1 pt-2 border-t border-slate-800">
                {LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => i18n.changeLanguage(code)}
                    className={`flex-1 py-2 rounded-lg text-xs font-mono font-semibold transition-colors ${
                      i18n.language === code
                        ? 'bg-primary-600 text-white'
                        : 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Spacer for double header */}
      <div className="h-[88px]" />
    </>
  );
}
